import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AffiliateProduct,
  getAffiliateCatalog,
  adminUpsertAffiliateProduct,
  UpsertAffiliateProductPayload,
  AffiliatePartner,
  adminListAffiliatePartners,
  adminReviewAffiliatePartner,
} from '../../../../api/affiliate/affiliate.api';
import { Check, Plus } from 'lucide-react';
import { AdminButton, AdminCard, AdminPageHeader, AdminSectionTitle, AdminStatusPill } from '../../ui';
import AdminEmpty from '../../components/AdminEmpty';
import { hasStrictPermission, PERMISSION } from '../../../../utils/admin-permissions';

const NoAccess: React.FC = () => (
  <div className="p-6 text-gray-500 dark:text-gray-400">Bạn không có quyền truy cập khu vực này.</div>
);

const empty: UpsertAffiliateProductPayload = {
  network: 'Manual',
  title: '',
  thumbnailUrl: '',
  price: 0,
  commissionRate: 0.1,
  originalAffiliateLink: '',
  isActive: true,
};

const ManagementAffiliateInner: React.FC = () => {
  const [form, setForm] = useState<UpsertAffiliateProductPayload>(empty);
  const [list, setList] = useState<AffiliateProduct[]>([]);
  const [saving, setSaving] = useState(false);
  const [partners, setPartners] = useState<AffiliatePartner[]>([]);
  const [busyPartner, setBusyPartner] = useState<string | null>(null);

  const load = async () => {
    try {
      setList(await getAffiliateCatalog(undefined, 0, 100));
    } catch {
      /* ignore */
    }
  };
  const loadPartners = () =>
    adminListAffiliatePartners('Pending').then(setPartners).catch(() => undefined);
  useEffect(() => {
    load();
    loadPartners();
  }, []);

  const reviewPartner = async (userId: string, approve: boolean) => {
    let reason: string | undefined;
    if (!approve) {
      const r = window.prompt('Lý do từ chối cộng tác viên:', 'Chưa đủ điều kiện');
      if (r === null) return;
      reason = r;
    }
    try {
      setBusyPartner(userId);
      await adminReviewAffiliatePartner(userId, approve, reason);
      toast.success(approve ? 'Đã duyệt cộng tác viên.' : 'Đã từ chối.');
      loadPartners();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Thao tác thất bại.');
    } finally {
      setBusyPartner(null);
    }
  };

  const set = (patch: Partial<UpsertAffiliateProductPayload>) =>
    setForm((p) => ({ ...p, ...patch }));

  const save = async () => {
    if (!form.title.trim() || !form.originalAffiliateLink.trim())
      return toast.error('Nhập tên + link affiliate gốc.');
    try {
      setSaving(true);
      await adminUpsertAffiliateProduct(form);
      toast.success('Đã lưu sản phẩm.');
      setForm(empty);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Lưu thất bại.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Cab Shop — Sản phẩm affiliate"
        description="Nhập tay khi chưa có API key Accesstrade/Ecomobi. (Đồng bộ tự động dùng cùng luồng này khi có key.)"
      />

      <div className="grid gap-6 items-start lg:grid-cols-2">
        {/* Duyệt đăng ký cộng tác viên */}
        <AdminCard
          title="Đăng ký cộng tác viên chờ duyệt"
          extra={<AdminStatusPill tone="warning">{partners.length}</AdminStatusPill>}
        >
          {partners.length === 0 ? (
            <p className="adm-muted" style={{ fontSize: 14, margin: 0 }}>Không có đăng ký nào chờ duyệt.</p>
          ) : (
            <div>
              {partners.map((p, idx) => (
                <div
                  key={p.userId}
                  className="flex items-center gap-3"
                  style={{ padding: '12px 0', borderTop: idx === 0 ? '1px solid var(--adm-line)' : undefined, borderBottom: '1px solid var(--adm-line)' }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--adm-heading)' }}>{p.userId}</div>
                    <div style={{ fontSize: 12, color: 'var(--adm-muted)' }}>
                      Đăng ký: {p.appliedAt ? new Date(p.appliedAt).toLocaleString('vi-VN') : '—'}
                      {p.note ? ` · ${p.note}` : ''}
                    </div>
                  </div>
                  <AdminButton size={32} variant="success" icon={<Check size={14} />} disabled={busyPartner === p.userId} onClick={() => reviewPartner(p.userId, true)}>
                    Duyệt
                  </AdminButton>
                  <AdminButton size={32} variant="danger-soft" disabled={busyPartner === p.userId} onClick={() => reviewPartner(p.userId, false)}>
                    Từ chối
                  </AdminButton>
                </div>
              ))}
            </div>
          )}
        </AdminCard>

        <AdminCard title="Thêm sản phẩm">
          <div className="grid md:grid-cols-2 gap-3">
            <input className="adm-input" placeholder="Tên sản phẩm" value={form.title} onChange={(e) => set({ title: e.target.value })} />
            <input className="adm-input" placeholder="Ảnh (URL)" value={form.thumbnailUrl} onChange={(e) => set({ thumbnailUrl: e.target.value })} />
            <input type="number" className="adm-input" placeholder="Giá (VND)" value={form.price} onChange={(e) => set({ price: Number(e.target.value) })} />
            <input type="number" step="0.01" className="adm-input" placeholder="Tỉ lệ hoa hồng (0.1 = 10%)" value={form.commissionRate} onChange={(e) => set({ commissionRate: Number(e.target.value) })} />
            <input className="adm-input md:col-span-2" placeholder="Link affiliate gốc (Accesstrade/Ecomobi/Shopee...)" value={form.originalAffiliateLink} onChange={(e) => set({ originalAffiliateLink: e.target.value })} />
            <select className="adm-select" value={form.network} onChange={(e) => set({ network: e.target.value })}>
              {['Manual', 'Accesstrade', 'Ecomobi'].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <AdminButton onClick={save} disabled={saving} icon={saving ? undefined : <Plus size={18} />} block>
              {saving ? 'Đang lưu…' : 'Thêm sản phẩm'}
            </AdminButton>
          </div>
        </AdminCard>
      </div>

      <section className="adm-stack">
        <AdminSectionTitle
          title="Sản phẩm affiliate"
          extra={<span className="adm-muted" style={{ fontSize: 14 }}>{list.length} sản phẩm</span>}
        />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((p) => (
            <AdminCard key={p.id} style={{ padding: 20, flexDirection: "row", alignItems: "center", gap: 12 }}>
              {p.thumbnailUrl ? (
                <img src={p.thumbnailUrl} alt="" className="w-12 h-12 object-cover shrink-0" style={{ borderRadius: 12 }} />
              ) : (
                <div className="w-12 h-12 shrink-0" style={{ borderRadius: 12, background: 'var(--adm-fill)' }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="truncate" style={{ fontSize: 14, fontWeight: 600, color: 'var(--adm-heading)' }}>{p.title}</div>
                <div style={{ fontSize: 12, color: 'var(--adm-muted)' }}>{(p.price || 0).toLocaleString('vi-VN')}đ · HH {(p.commissionRate * 100).toFixed(0)}% · {p.network}</div>
              </div>
            </AdminCard>
          ))}
        </div>
        {list.length === 0 && <AdminEmpty>Chưa có sản phẩm.</AdminEmpty>}
      </section>
    </div>
  );
};

const ManagementAffiliate: React.FC = () =>
  hasStrictPermission(PERMISSION.AffiliateManage) ? <ManagementAffiliateInner /> : <NoAccess />;

export default ManagementAffiliate;
